import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMenu } from "@/components/BrandMenu";
import { Mail, Send, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return toast.error("Please fill in every field");
    setSending(true);
    try {
      await api.post("/contact", { name: name.trim(), email: email.trim(), message: message.trim() });
      setSent(true);
    } catch {
      toast.error("Couldn't send your message. Please email support@unveil.it.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandMenu />
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="rounded-full border-white/20 bg-transparent"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-6 py-16">
        <p className="text-xs tracking-[0.3em] uppercase text-gold font-bold mb-2">Get in touch</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-3">Contact us</h1>
        <p className="text-foreground/60 mb-10">Questions, feedback or need a hand with a reveal? Send us a message and we'll reply to your email. You can also reach us directly at <a href="mailto:support@unveil.it.com" className="text-gold hover:underline">support@unveil.it.com</a>.</p>

        {sent ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-forest/30 bg-forest/10 p-8 text-center" data-testid="contact-success">
            <CheckCircle2 className="w-12 h-12 text-forest mx-auto mb-4" />
            <h2 className="font-display font-bold text-2xl mb-2">Message sent!</h2>
            <p className="text-foreground/60 mb-6">Thanks for reaching out — we'll get back to you at {email} soon.</p>
            <Button onClick={() => navigate("/")} className="rounded-full bg-gold hover:bg-gold/90 text-black font-semibold h-11 px-6">Back to home</Button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">Your name</Label>
              <Input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="mt-1.5 h-12 bg-card border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">Email</Label>
              <Input data-testid="contact-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-12 bg-card border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-foreground/60">Message</Label>
              <Textarea data-testid="contact-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} placeholder="How can we help?" className="mt-1.5 bg-card border-white/10" />
            </div>
            <Button type="submit" data-testid="contact-submit-btn" disabled={sending} className="w-full h-12 rounded-full bg-gold hover:bg-gold/90 text-black font-semibold">
              {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-2" /> Send message</>)}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
